import { z } from "zod";
import {
  ISavedBlockApi,
  SavedBlockApiSchema,
  SavedBlocksApiSchema,
} from "@/schemas/savedBlock/savedBlockApiSchema";

export interface SkippedSavedBlock {
  id: number | null;
  name: string | null;
  issues: z.ZodIssue[];
}

export interface ParsedSavedBlocks {
  blocks: ISavedBlockApi[];
  skipped: SkippedSavedBlock[];
}

/**
 * Parses the saved blocks list. The whole array is validated strictly first;
 * only when that fails are the items parsed one by one. Saved blocks are
 * end-user data, so one corrupted record (e.g. `definition: {}`) must not hide
 * the whole library – invalid items are dropped and reported in `skipped` so
 * the caller can notify the user. Returns null when the payload is not a list.
 */
export function parseSavedBlocks(raw: unknown): ParsedSavedBlocks | null {
  if (!Array.isArray(raw)) return null;

  const whole = SavedBlocksApiSchema.safeParse(raw);
  if (whole.success) return { blocks: whole.data, skipped: [] };

  const blocks: ISavedBlockApi[] = [];
  const skipped: SkippedSavedBlock[] = [];

  for (const item of raw) {
    const result = SavedBlockApiSchema.safeParse(item);
    if (result.success) {
      blocks.push(result.data);
      continue;
    }
    const record =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    skipped.push({
      id: typeof record.id === "number" ? record.id : null,
      name: typeof record.name === "string" ? record.name : null,
      issues: result.error.issues,
    });
  }

  return { blocks, skipped };
}

export function describeSkippedSavedBlocks(skipped: SkippedSavedBlock[]) {
  return skipped
    .map((block) => {
      if (block.name && block.id !== null)
        return `${block.name} (#${block.id})`;
      if (block.name) return block.name;
      if (block.id !== null) return `#${block.id}`;
      return "?";
    })
    .join(", ");
}
